/**
 * useFeedback Hook
 *
 * Provides feedback functionality with automatic backend sync.
 * Submits message-level feedback to the backend API for RLHF data collection.
 */

import { useCallback, useEffect, useRef } from 'react'
import { useFeedbackStore } from '@/stores/feedbackStore'
import type { MessageFeedbackData } from '@/components/feedback'
import type { SessionFeedbackData } from '@/components/feedback'
import { apiClient } from '@/services/apiClient'


interface UseFeedbackOptions {
  // Conversation ID for message feedback
  conversationId?: string | null
  // Auto-sync interval in ms (0 to disable)
  syncInterval?: number
  // Callback when feedback is submitted
  onFeedbackSubmit?: (type: 'message' | 'session', success: boolean) => void
}

export function useFeedback(options: UseFeedbackOptions = {}) {
  const {
    conversationId,
    syncInterval = 30000, // 30 seconds
    onFeedbackSubmit,
  } = options

  const {
    messageFeedback,
    sessionFeedback,
    pendingSyncCount,
    stats,
    addMessageFeedback,
    addSessionFeedback,
    getMessageFeedback,
    getSessionFeedback,
    markSynced,
    getPendingFeedback,
  } = useFeedbackStore()

  const syncInProgressRef = useRef(false)

  // Sync message feedback to backend
  const syncMessageFeedback = useCallback(
    async (feedback: MessageFeedbackData, convId: string) => {
      if (!feedback.rating) return false

      try {
        const response = await apiClient.fetch(`/api/feedback`, {
          method: 'POST',
          body: JSON.stringify({
            message_id: feedback.messageId,
            conversation_id: convId,
            rating: feedback.rating,
            comment: feedback.comment || null,
            categories: null,
          }),
        })

        if (response.ok) {
          markSynced('message', feedback.messageId)
          onFeedbackSubmit?.('message', true)
          return true
        } else if (response.status === 409) {
          // Feedback already exists - try to update
          const existingResponse = await apiClient.fetch(
            `/api/feedback/message/${feedback.messageId}`
          )
          if (existingResponse.ok) {
            const existing = await existingResponse.json()
            if (existing?.id) {
              const updateResponse = await apiClient.fetch(
                `/api/feedback/${existing.id}`,
                {
                  method: 'PUT',
                  body: JSON.stringify({
                    rating: feedback.rating,
                    comment: feedback.comment || null,
                  }),
                }
              )
              if (updateResponse.ok) {
                markSynced('message', feedback.messageId)
                onFeedbackSubmit?.('message', true)
                return true
              }
            }
          }
          console.warn('Failed to update existing feedback')
          onFeedbackSubmit?.('message', false)
          return false
        } else {
          console.warn('Failed to sync message feedback:', response.statusText)
          onFeedbackSubmit?.('message', false)
          return false
        }
      } catch (error) {
        console.warn('Error syncing message feedback:', error)
        onFeedbackSubmit?.('message', false)
        return false
      }
    },
    [markSynced, onFeedbackSubmit]
  )

  // Submit message feedback
  const submitMessageFeedback = useCallback(
    (feedback: MessageFeedbackData, convId?: string) => {
      addMessageFeedback(feedback)
      // Attempt immediate sync if we have a conversation ID
      const effectiveConvId = convId || conversationId
      if (effectiveConvId) {
        syncMessageFeedback(feedback, effectiveConvId)
      }
    },
    [addMessageFeedback, conversationId, syncMessageFeedback]
  )

  // Submit session feedback (stored locally, no backend sync yet)
  const submitSessionFeedback = useCallback(
    (feedback: SessionFeedbackData) => {
      addSessionFeedback(feedback)
      // Session feedback is stored locally for now
      // Backend sync can be added later if needed
      onFeedbackSubmit?.('session', true)
    },
    [addSessionFeedback, onFeedbackSubmit]
  )

  // Batch sync all pending message feedback
  const syncAllPending = useCallback(async () => {
    if (syncInProgressRef.current || !conversationId) return

    syncInProgressRef.current = true
    const pending = getPendingFeedback()

    try {
      // Sync message feedback
      for (const feedback of pending.messages) {
        await syncMessageFeedback(feedback, conversationId)
      }
    } finally {
      syncInProgressRef.current = false
    }
  }, [conversationId, getPendingFeedback, syncMessageFeedback])

  // Auto-sync on interval
  useEffect(() => {
    if (syncInterval <= 0 || !conversationId) return

    const intervalId = setInterval(() => {
      if (pendingSyncCount > 0) {
        syncAllPending()
      }
    }, syncInterval)

    return () => clearInterval(intervalId)
  }, [syncInterval, pendingSyncCount, syncAllPending, conversationId])

  return {
    // State
    pendingSyncCount,
    stats,

    // Message feedback
    submitMessageFeedback,
    getMessageFeedback,

    // Session feedback
    submitSessionFeedback,
    getSessionFeedback,

    // Sync
    syncAllPending,

    // Check if a message has feedback
    hasMessageFeedback: (messageId: string) => !!messageFeedback[messageId],

    // Check if a session has feedback
    hasSessionFeedback: (sessionId: string) => !!sessionFeedback[sessionId],
  }
}
